import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Typography,
  TextField,
  InputAdornment,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Menu,
  MenuItem,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  Checkbox,
  FormControlLabel,
  LinearProgress,
} from '@mui/material';
import {
  Add as AddIcon,
  Search as SearchIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  MoreVert as MoreVertIcon,
  Print as PrintIcon,
  Email as EmailIcon,
  Download as DownloadIcon,
  Visibility as ViewIcon,
  FilterList as FilterListIcon,
  Clear as ClearIcon,
} from '@mui/icons-material';
import api from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { useSettings } from '../../context/SettingsContext';

// No mock data - using database data only

const InvoiceList: React.FC = () => {
  const navigate = useNavigate();
  const { isAuthenticated, isLoading: authLoading, hasPermission } = useAuth();
  const { formatCurrency } = useSettings();
  
  const [invoices, setInvoices] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [invoiceToDelete, setInvoiceToDelete] = useState<any | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedInvoice, setSelectedInvoice] = useState<any | null>(null);

  // Date range filter states
  const [fromDate, setFromDate] = useState<string>('');
  const [toDate, setToDate] = useState<string>('');
  const [useInvoiceDate, setUseInvoiceDate] = useState<boolean>(true);
  const [statusFilter, setStatusFilter] = useState<string>('all');

  // Download progress states
  const [downloading, setDownloading] = useState(false);
  const [downloadProgress, setDownloadProgress] = useState<{ current: number; total: number } | null>(null);
  
  useEffect(() => {
    const fetchInvoices = async () => {
      setLoading(true);
      try {
        const response = await api.get('/invoices');
        if (response.data && response.data.success) {
          setInvoices(response.data.data);
        } else {
          console.warn('No invoice data received from API');
          setInvoices([]);
        }
      } catch (error) {
        console.error('Error fetching invoices:', error);
        setInvoices([]);
      } finally {
        setLoading(false);
      }
    };
    
    if (!authLoading && isAuthenticated) {
      fetchInvoices();
    }
  }, [authLoading, isAuthenticated]);
  
  const handleAddInvoice = () => {
    navigate('/invoices/add');
  };
  
  const handleEditInvoice = (invoice: any) => {
    navigate(`/invoices/edit/${invoice.id}`);
  };
  
  const handleViewInvoice = (invoice: any) => {
    navigate(`/invoices/${invoice.id}`);
  };
  
  const handleDeleteClick = (invoice: any) => {
    setInvoiceToDelete(invoice);
    setDeleteDialogOpen(true);
  };
  
  const handleDeleteConfirm = async () => {
    if (!invoiceToDelete) return;
    
    try {
      const response = await api.delete(`/invoices/${invoiceToDelete.id}`);
      if (response.data && response.data.success) {
        setInvoices(invoices.filter(i => i.id !== invoiceToDelete.id));
        setDeleteDialogOpen(false);
      } else {
        console.error('Failed to delete invoice');
      }
      setInvoiceToDelete(null);
    } catch (error) {
      console.error('Error deleting invoice:', error);
    }
  };
  
  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, invoice: any) => {
    setAnchorEl(event.currentTarget);
    setSelectedInvoice(invoice);
  };
  
  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedInvoice(null);
  };
  
  const handlePrintInvoice = () => {
    handleMenuClose();
    // In a real application, we would implement print functionality
    console.log('Print invoice:', selectedInvoice?.invoiceNumber);
  };
  
  const handleEmailInvoice = () => {
    handleMenuClose();
    // In a real application, we would implement email functionality
    console.log('Email invoice:', selectedInvoice?.invoiceNumber);
  };
  
  const handleDownloadInvoice = async () => {
    if (!selectedInvoice) return;

    handleMenuClose();

    try {
      console.log('Starting PDF download for invoice ID:', selectedInvoice.id);

      const response = await api.get(`/invoices/${selectedInvoice.id}/pdf-kit`, {
        responseType: 'blob',
        headers: {
          'Accept': 'application/pdf'
        }
      });

      if (!response.data) {
        throw new Error('No PDF data received from server');
      }

      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `invoice-${selectedInvoice.invoiceNumber || selectedInvoice.invoice_number}.pdf`;

      document.body.appendChild(link);
      link.click();

      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      console.log('Invoice downloaded successfully');
    } catch (error: any) {
      console.error('Error downloading invoice:', error);
      alert(`Failed to download invoice: ${error?.message || 'Unknown error occurred'}`);
    }
  };
  
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'paid':
        return 'success';
      case 'pending':
        return 'warning';
      case 'overdue':
        return 'error';
      case 'draft':
        return 'default';
      default:
        return 'default';
    }
  };
  
  const formatDate = (dateString: string | undefined) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  };

  const handleClearFilters = () => {
    setFromDate('');
    setToDate('');
    setStatusFilter('all');
    setUseInvoiceDate(true);
  };

  const handleDownloadAll = async () => {
    if (filteredInvoices.length === 0) return;

    setDownloading(true);
    setDownloadProgress({ current: 0, total: filteredInvoices.length });

    try {
      const params = new URLSearchParams();
      if (fromDate) params.append('startDate', fromDate);
      if (toDate) params.append('endDate', toDate);
      params.append('dateType', useInvoiceDate ? 'invoice_date' : 'order_date');
      if (statusFilter !== 'all') params.append('status', statusFilter);

      const response = await api.get(`/invoices/download-bulk?${params.toString()}`, {
        responseType: 'blob',
        headers: {
          'Accept': 'application/zip'
        },
        onDownloadProgress: (progressEvent) => {
          // Update progress based on download
          if (progressEvent.total) {
            const percentComplete = Math.round((progressEvent.loaded / progressEvent.total) * 100);
            // Estimate current invoice based on percentage
            const estimatedCurrent = Math.floor((percentComplete / 100) * filteredInvoices.length);
            setDownloadProgress({ current: estimatedCurrent, total: filteredInvoices.length });
          }
        }
      });

      // Create blob from response data
      const blob = new Blob([response.data], { type: 'application/zip' });

      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;

      // Generate filename with date range
      const fromDateStr = fromDate || 'all';
      const toDateStr = toDate || 'all';
      link.download = `invoices-${fromDateStr}-to-${toDateStr}.zip`;

      // Trigger download
      document.body.appendChild(link);
      link.click();

      // Cleanup
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      console.log('Invoices downloaded successfully');
    } catch (error: any) {
      console.error('Error downloading invoices:', error);
      alert(`Failed to download invoices: ${error?.message || 'Unknown error occurred'}`);
    } finally {
      setDownloading(false);
      setDownloadProgress(null);
    }
  };
  
  const filteredInvoices = invoices.filter(invoice => {
    const searchTermLower = searchTerm.toLowerCase();

    // Search term filter
    const matchesSearch = (
      // Check invoice number (could be invoiceNumber or invoice_number)
      ((invoice.invoiceNumber || invoice.invoice_number || '').toLowerCase().includes(searchTermLower)) ||
      // Check order number (could be orderNumber or order_number)
      ((invoice.orderNumber || invoice.order_number || '').toLowerCase().includes(searchTermLower)) ||
      // Check customer name (could be customerName or customer_name)
      ((invoice.customerName || invoice.customer_name || '').toLowerCase().includes(searchTermLower))
    );

    // Status filter
    const matchesStatus = statusFilter === 'all' || invoice.status === statusFilter;

    // Date range filter
    let matchesDateRange = true;
    if (fromDate || toDate) {
      const dateToCheck = useInvoiceDate
        ? (invoice.issueDate || invoice.issue_date || invoice.invoiceDate || invoice.invoice_date)
        : (invoice.orderDate || invoice.order_date);

      if (dateToCheck) {
        const invoiceDate = new Date(dateToCheck);
        if (fromDate) {
          const from = new Date(fromDate);
          from.setHours(0, 0, 0, 0);
          if (invoiceDate < from) matchesDateRange = false;
        }
        if (toDate) {
          const to = new Date(toDate);
          to.setHours(23, 59, 59, 999);
          if (invoiceDate > to) matchesDateRange = false;
        }
      } else {
        matchesDateRange = false;
      }
    }

    return matchesSearch && matchesStatus && matchesDateRange;
  });
  
  if (loading && invoices.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
        <CircularProgress />
      </Box>
    );
  }
  
  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">Invoices</Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          {hasPermission('invoices.create') && (
          <Button
            variant="outlined"
            startIcon={<DownloadIcon />}
            onClick={handleDownloadAll}
            disabled={(!fromDate && !toDate) || filteredInvoices.length === 0 || downloading}
          >
            Download All ({filteredInvoices.length})
          </Button>
          )}
          {/* Create Invoice button removed - invoices are generated from orders only */}
        </Box>
      </Box>

      {/* Filter Section */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <FilterListIcon sx={{ mr: 1, color: 'text.secondary' }} />
          <Typography variant="h6">Filters</Typography>
        </Box>

        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(4, 1fr)' }, gap: 2, mb: 2 }}>
          {/* From Date */}
          <TextField
            label="From Date"
            type="date"
            value={fromDate}
            onChange={(e) => setFromDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            InputProps={{
              sx: {
                '& input[type="date"]::-webkit-calendar-picker-indicator': {
                  cursor: 'pointer'
                },
                '& input[type="date"]::-webkit-datetime-edit': {
                  color: fromDate ? 'inherit' : '#9e9e9e'
                },
                '& input[type="date"]::-webkit-datetime-edit-fields-wrapper': {
                  color: fromDate ? 'inherit' : '#9e9e9e'
                }
              }
            }}
            size="small"
            fullWidth
            onClick={(e) => {
              const input = e.currentTarget.querySelector('input');
              if (input) input.showPicker?.();
            }}
            sx={{ cursor: 'pointer' }}
          />

          {/* To Date */}
          <TextField
            label="To Date"
            type="date"
            value={toDate}
            onChange={(e) => setToDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            InputProps={{
              sx: {
                '& input[type="date"]::-webkit-calendar-picker-indicator': {
                  cursor: 'pointer'
                },
                '& input[type="date"]::-webkit-datetime-edit': {
                  color: toDate ? 'inherit' : '#9e9e9e'
                },
                '& input[type="date"]::-webkit-datetime-edit-fields-wrapper': {
                  color: toDate ? 'inherit' : '#9e9e9e'
                }
              }
            }}
            size="small"
            fullWidth
            onClick={(e) => {
              const input = e.currentTarget.querySelector('input');
              if (input) input.showPicker?.();
            }}
            sx={{ cursor: 'pointer' }}
          />

          {/* Status Filter */}
          <FormControl size="small" fullWidth>
            <InputLabel>Status</InputLabel>
            <Select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              label="Status"
            >
              <MenuItem value="all">All</MenuItem>
              <MenuItem value="draft">Draft</MenuItem>
              <MenuItem value="pending">Pending</MenuItem>
              <MenuItem value="paid">Paid</MenuItem>
              <MenuItem value="overdue">Overdue</MenuItem>
              <MenuItem value="cancelled">Cancelled</MenuItem>
            </Select>
          </FormControl>

          {/* Clear Filters Button */}
          <Button
            variant="outlined"
            startIcon={<ClearIcon />}
            onClick={handleClearFilters}
            fullWidth
          >
            Clear Filters
          </Button>
        </Box>

        {/* Date Type Checkboxes */}
        <Box sx={{ display: 'flex', gap: 3, alignItems: 'center' }}>
          <Typography variant="body2" color="text.secondary">Filter by:</Typography>
          <FormControlLabel
            control={
              <Checkbox
                checked={useInvoiceDate}
                onChange={(e) => {
                  if (e.target.checked) {
                    setUseInvoiceDate(true);
                  }
                }}
              />
            }
            label="Invoice Date"
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={!useInvoiceDate}
                onChange={(e) => {
                  if (e.target.checked) {
                    setUseInvoiceDate(false);
                  }
                }}
              />
            }
            label="Order Date"
          />
        </Box>

        {/* Filter Results Summary */}
        {(fromDate || toDate || statusFilter !== 'all') && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" color="text.secondary">
              Showing {filteredInvoices.length} invoice{filteredInvoices.length !== 1 ? 's' : ''}
              {fromDate && ` from ${new Date(fromDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`}
              {toDate && ` to ${new Date(toDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`}
              {statusFilter !== 'all' && ` • Status: ${statusFilter.charAt(0).toUpperCase() + statusFilter.slice(1)}`}
            </Typography>
          </Box>
        )}
      </Paper>

      <Box sx={{ mb: 3 }}>
        <TextField
          fullWidth
          placeholder="Search invoices by number, order, or customer"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
          variant="outlined"
          size="small"
        />
      </Box>
      
      {filteredInvoices.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography variant="h6" color="text.secondary">
            No invoices found
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Try adjusting your search criteria
          </Typography>
        </Box>
      ) : (
        <TableContainer component={Paper}>
          <Table sx={{ minWidth: 650 }}>
            <TableHead>
              <TableRow>
                <TableCell>Invoice #</TableCell>
                <TableCell>Order #</TableCell>
                <TableCell>Customer</TableCell>
                <TableCell>Issue Date</TableCell>
                <TableCell>Due Date</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Amount</TableCell>
                <TableCell align="right">Tax</TableCell>
                <TableCell align="right">Total</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredInvoices.map((invoice) => (
                <TableRow key={invoice.id} onClick={() => handleViewInvoice(invoice)} sx={{ cursor: 'pointer', '&:hover': { backgroundColor: 'action.hover' } }}>
                  <TableCell>{invoice.invoiceNumber || invoice.invoice_number || 'N/A'}</TableCell>
                  <TableCell>{invoice.orderNumber || invoice.order_number || 'N/A'}</TableCell>
                  <TableCell>{invoice.customerName || invoice.customer_name || 'N/A'}</TableCell>
                  <TableCell>{formatDate(invoice.issueDate || invoice.issue_date)}</TableCell>
                  <TableCell>{formatDate(invoice.dueDate || invoice.due_date)}</TableCell>
                  <TableCell>
                    <Chip 
                      label={((invoice.status || 'unknown').charAt(0).toUpperCase() + (invoice.status || 'unknown').slice(1))} 
                      size="small"
                      color={getStatusColor(invoice.status || 'unknown')}
                    />
                  </TableCell>
                  <TableCell align="right">{formatCurrency(Number(invoice.subtotal) || 0)}</TableCell>
                  <TableCell align="right">{formatCurrency(Number(invoice.tax_amount) || 0)}</TableCell>
                  <TableCell align="right">
                    <Tooltip title="Total amount calculated from invoice items minus any refunds">
                      <Box component="span">{formatCurrency(Number(invoice.total_amount) || 0)}</Box>
                    </Tooltip>
                  </TableCell>
                  <TableCell align="center" onClick={(e) => e.stopPropagation()}>
                    <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                      <IconButton
                        size="small"
                        onClick={() => handleViewInvoice(invoice)}
                        color="primary"
                      >
                        <ViewIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={async () => {
                          try {
                            console.log('Starting PDF download for invoice ID:', invoice.id);

                            const response = await api.get(`/invoices/${invoice.id}/pdf-kit`, {
                              responseType: 'blob',
                              headers: {
                                'Accept': 'application/pdf'
                              }
                            });

                            if (!response.data) {
                              throw new Error('No PDF data received from server');
                            }

                            const blob = new Blob([response.data], { type: 'application/pdf' });
                            const url = window.URL.createObjectURL(blob);
                            const link = document.createElement('a');
                            link.href = url;
                            link.download = `invoice-${invoice.invoiceNumber || invoice.invoice_number}.pdf`;

                            document.body.appendChild(link);
                            link.click();

                            document.body.removeChild(link);
                            window.URL.revokeObjectURL(url);

                            console.log('Invoice downloaded successfully');
                          } catch (error: any) {
                            console.error('Error downloading invoice:', error);
                            alert(`Failed to download invoice: ${error?.message || 'Unknown error occurred'}`);
                          }
                        }}
                        color="primary"
                      >
                        <DownloadIcon fontSize="small" />
                      </IconButton>
                      {hasPermission('invoices.delete') && (
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleDeleteClick(invoice)}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                      )}
                      {/* Three-dot menu removed - no active menu items */}
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      
      {/* Invoice actions menu */}
      {/* Actions menu removed - Print and Email were not implemented */}
      
      {/* Delete confirmation dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle>Delete Invoice</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete invoice "{invoiceToDelete?.invoiceNumber}"? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDeleteConfirm} color="error">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Download Progress Dialog */}
      <Dialog
        open={downloading}
        disableEscapeKeyDown
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Downloading Invoices</DialogTitle>
        <DialogContent>
          <Box sx={{ py: 2 }}>
            {downloadProgress && (
              <>
                <Typography variant="body1" gutterBottom align="center">
                  Generating {downloadProgress.current} of {downloadProgress.total} PDFs...
                </Typography>
                <LinearProgress
                  variant="determinate"
                  value={(downloadProgress.current / downloadProgress.total) * 100}
                  sx={{ mt: 2, mb: 2 }}
                />
                <Typography variant="body2" color="warning.main" align="center" sx={{ mt: 2 }}>
                  ⚠️ Please do not close this window or navigate away until download completes.
                </Typography>
              </>
            )}
          </Box>
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default InvoiceList;
